import {
  Box,
  Flex,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalOverlay,
  Text
} from '@chakra-ui/react';
import { ICOCConfig, IPermission } from '@publishing-engine/domain/models';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  fetchWorkspaceRoles,
  fetchSubmissionContextMemberships
} from 'src/api/queries';
import { useUpdateJournalSettings } from 'src/api/mutations';
import { Button as MRButton, OutlineCard, useToast } from '@morressier/alembic';
import { faTrash } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import RoleFormModal from './RoleFormModal';
import OwnerSection from './OwnerSection';
import OtherRolesSection from './OtherRolesSection';
import {
  COCRecommendationLockReason,
  ModalState,
  OwnerEntry,
  OtherRoleEntry
} from './types';

// ---- Re-export Decision type for backward compatibility ----
export type { Decision } from './types';

// ---- Props ----

interface Props {
  initialConfig?: ICOCConfig[];
  journalId: string;
  workspaceId: string;
  isCOCRecommendationRequired?: boolean;
  /** Set when the recommendation toggle is frozen; see COCRecommendationLockReason. */
  recommendationLockReason?: COCRecommendationLockReason;
  /** Journal is active or has submissions — saved role max limits may only grow. */
  isJournalLocked?: boolean;
}

// ---- Helpers ----

/**
 * Roles that are never offered as COC roles: read-only roles, i.e. every work
 * phase they are configured for grants no more than the "viewer" level.
 * Example: "Observer", which is "viewer" across all phases.
 */
const isViewerOnlyRole = (permissions?: IPermission): boolean => {
  const levels = Object.values(permissions ?? {});
  return levels.length > 0 && levels.every(level => level === 'viewer');
};

const INITIAL_MODAL_STATE: ModalState = {
  isOpen: false,
  mode: 'add',
  type: 'owner'
};

// ---- Main component ----

const CocConfiguration: React.FC<Props> = ({
  initialConfig,
  journalId,
  workspaceId,
  isCOCRecommendationRequired,
  recommendationLockReason,
  isJournalLocked
}) => {
  const toast = useToast();

  // ---- Recommendation requirement state (local so the switch updates immediately) ----
  const [isRecommendationRequired, setIsRecommendationRequired] = useState(
    !!isCOCRecommendationRequired
  );

  useEffect(() => {
    setIsRecommendationRequired(!!isCOCRecommendationRequired);
  }, [isCOCRecommendationRequired]);

  // ---- Modal state (single source of truth) ----
  const [modal, setModal] = useState<ModalState>(INITIAL_MODAL_STATE);

  // ---- Delete confirmation state ----
  const [deleteConfirm, setDeleteConfirm] = useState<{
    isOpen: boolean;
    entry: { label: string; onConfirm: () => Promise<void> } | null;
  }>({ isOpen: false, entry: null });

  // ---- Fetch available workspace roles ----
  const { data: orgRoles } = useQuery({
    queryKey: ['workspace-roles', { workspaceId }],
    queryFn: () => (workspaceId ? fetchWorkspaceRoles(workspaceId) : null)
  });

  const availableRoles = useMemo(() => {
    const roleConfigs = orgRoles?.roleConfigs ?? {};
    const roles = Object.keys(roleConfigs).filter(
      role => !isViewerOnlyRole(roleConfigs[role])
    );

    // Keep the role currently being edited selectable even when excluded, so
    // entries saved before the exclusion still render instead of going blank.
    const editingRoleName = modal.editingRole?.role;
    if (editingRoleName && !roles.includes(editingRoleName)) {
      roles.push(editingRoleName);
    }

    return roles;
  }, [orgRoles, modal.editingRole?.role]);

  // Fetch all memberships (unbounded) to know which roles are currently assigned.
  const { data: membershipsData } = useQuery({
    queryKey: ['journal-memberships-coc', { journalId }],
    queryFn: () =>
      fetchSubmissionContextMemberships(journalId, { limit: 9999, offset: 0 }),
    retry: 0
  });

  /** Role names that have at least one member assigned */
  const assignedRoleNames = useMemo(
    () =>
      new Set(
        (membershipsData?.collection ?? []).map(m => m.roleName).filter(Boolean)
      ),
    [membershipsData]
  );

  // ---- Derived: split config into owner + other roles ----
  const owner = useMemo<OwnerEntry | null>(() => {
    if (!initialConfig?.length) return null;
    const config = initialConfig.find(c => c.isOwner);
    if (!config) return null;
    const idx = initialConfig.indexOf(config);
    return { config, originalIndex: idx };
  }, [initialConfig]);

  const otherRoles = useMemo<OtherRoleEntry[]>(() => {
    if (!initialConfig?.length) return [];
    return initialConfig.reduce<OtherRoleEntry[]>((acc, config, idx) => {
      if (!config.isOwner) {
        acc.push({ config, originalIndex: idx });
      }
      return acc;
    }, []);
  }, [initialConfig]);

  /** Currently assigned role levels from other roles (owner is always level 1) */
  const assignedRoleLevels = useMemo(
    () =>
      otherRoles
        .map(r => r.config.roleLevel)
        .filter((l): l is number => l !== null && l !== undefined),
    [otherRoles]
  );

  /** Role names already taken by other roles — exclude from owner dropdown */
  const otherRoleNames = useMemo(
    () => otherRoles.map(r => r.config.role).filter(Boolean),
    [otherRoles]
  );

  /** Owner role name — exclude from other-roles dropdown */
  const ownerRoleName = owner?.config.role;

  // ---- Mutation ----
  const updateSettingMutation = useUpdateJournalSettings(journalId);
  const isSaving = updateSettingMutation.isPending;

  // ---- Build full cocConfig array for API payload ----
  const currentConfig = useMemo(() => initialConfig ?? [], [initialConfig]);

  // ---- Handlers ----

  const openModal = useCallback(
    (
      mode: ModalState['mode'],
      type: ModalState['type'],
      editingRole?: ICOCConfig,
      editingIndex?: number
    ) => {
      setModal({
        isOpen: true,
        mode,
        type,
        editingRole,
        editingIndex
      });
    },
    []
  );

  const closeModal = useCallback(() => {
    setModal(INITIAL_MODAL_STATE);
  }, []);

  // ---- Owners ----

  const handleAddOwner = useCallback(() => {
    openModal('add', 'owner');
  }, [openModal]);

  const handleEditOwner = useCallback(() => {
    if (!owner) return;
    openModal('edit', 'owner', owner.config, owner.originalIndex);
  }, [owner, openModal]);

  const handleRequestDeleteOwner = useCallback(() => {
    if (!owner) return;
    if (assignedRoleNames.has(owner.config.role)) {
      toast({
        variant: 'error',
        title: 'Cannot delete this role',
        description:
          'This role is currently assigned to members. Please unassign the members first before removing this role.',
        isClosable: true
      });
      return;
    }
    setDeleteConfirm({
      isOpen: true,
      entry: {
        label: `the owner role "${owner.config.role}"`,
        onConfirm: async () => {
          const newConfig = currentConfig.filter(
            (_, i) => i !== owner.originalIndex
          );

          const res = await updateSettingMutation.mutateAsync({
            data: {},
            config: { cocConfig: newConfig }
          } as any);

          if (res.isSuccess) {
            toast({
              variant: 'success',
              title: 'Owner removed successfully',
              isClosable: true
            });
            setDeleteConfirm({ isOpen: false, entry: null });
            return;
          }

          toast({
            variant: 'error',
            title:
              typeof res?.message === 'string'
                ? res.message
                : 'Failed to remove owner',
            isClosable: true
          });
        }
      }
    });
  }, [owner, assignedRoleNames, currentConfig, updateSettingMutation, toast]);

  // ---- Other Roles ----

  const handleAddOtherRole = useCallback(() => {
    openModal('add', 'other');
  }, [openModal]);

  const handleEditOtherRole = useCallback(
    (entry: OtherRoleEntry) => {
      openModal('edit', 'other', entry.config, entry.originalIndex);
    },
    [openModal]
  );

  const handleRequestDeleteOtherRole = useCallback(
    (entry: OtherRoleEntry) => {
      if (assignedRoleNames.has(entry.config.role)) {
        toast({
          variant: 'error',
          title: 'Cannot delete this role',
          description:
            'This role is currently assigned to members. Please unassign the members first before removing this role.',
          isClosable: true
        });
        return;
      }
      setDeleteConfirm({
        isOpen: true,
        entry: {
          label: `the role "${entry.config.role}"`,
          onConfirm: async () => {
            const newConfig = currentConfig.filter(
              (_, i) => i !== entry.originalIndex
            );

            const res = await updateSettingMutation.mutateAsync({
              data: {},
              config: { cocConfig: newConfig }
            } as any);

            if (res.isSuccess) {
              toast({
                variant: 'success',
                title: 'Role removed successfully',
                isClosable: true
              });
              setDeleteConfirm({ isOpen: false, entry: null });
              return;
            }

            toast({
              variant: 'error',
              title:
                typeof res?.message === 'string'
                  ? res.message
                  : 'Failed to remove role',
              isClosable: true
            });
          }
        }
      });
    },
    [assignedRoleNames, currentConfig, updateSettingMutation, toast]
  );

  // ---- Shared save handler ----

  const handleSave = useCallback(
    async (role: ICOCConfig) => {
      let newConfig: ICOCConfig[];

      if (modal.mode === 'add') {
        // Append new role to existing config
        newConfig = [...currentConfig, role];
      } else {
        // Replace existing role at its original index
        if (modal.editingIndex === undefined) return;
        newConfig = [...currentConfig];
        newConfig[modal.editingIndex] = role;
      }

      const res = await updateSettingMutation.mutateAsync({
        data: {},
        config: { cocConfig: newConfig }
      } as any);

      if (res.isSuccess) {
        toast({
          variant: 'success',
          title:
            modal.mode === 'add'
              ? 'Role added successfully'
              : 'Role updated successfully',
          isClosable: true
        });
        closeModal();
        return;
      }

      toast({
        variant: 'error',
        title:
          typeof res?.message === 'string'
            ? res.message
            : 'Something went wrong',
        isClosable: true
      });
    },
    [modal, currentConfig, updateSettingMutation, toast, closeModal]
  );

  // ---- Recommendation requirement toggle ----

  const handleToggleRecommendationRequired = useCallback(
    async (checked: boolean) => {
      setIsRecommendationRequired(checked);

      const res = await updateSettingMutation.mutateAsync({
        data: {},
        config: { isCOCRecommendationRequired: checked }
      } as any);

      if (res.isSuccess) {
        toast({
          variant: 'success',
          title: checked
            ? 'Recommendations enabled'
            : 'Recommendations disabled',
          isClosable: true
        });
        return;
      }

      // Revert on failure
      setIsRecommendationRequired(!checked);
      toast({
        variant: 'error',
        title:
          typeof res?.message === 'string'
            ? res.message
            : 'Something went wrong',
        isClosable: true
      });
    },
    [updateSettingMutation, toast]
  );

  // ---- Render ----

  return (
    <Flex flexDirection={'column'} gap={8}>
      <Text textStyle={'headingSection'}>Role Assignments</Text>
      <OutlineCard borderless paddingX={4} paddingY={6}>
        <Flex flexDirection={'column'} padding={2} gap={8}>
          {/* Section 1: Owner */}
          <OwnerSection
            owner={owner}
            onAdd={handleAddOwner}
            onEdit={handleEditOwner}
            onDelete={handleRequestDeleteOwner}
          />

          {/* Section 2: Other Roles */}
          <OtherRolesSection
            roles={otherRoles}
            onAdd={handleAddOtherRole}
            onEdit={handleEditOtherRole}
            onDelete={handleRequestDeleteOtherRole}
            isCOCRecommendationRequired={isRecommendationRequired}
            onToggleRecommendationRequired={handleToggleRecommendationRequired}
            recommendationLockReason={recommendationLockReason}
          />
        </Flex>

        {/* Shared RoleFormModal */}
        {modal.isOpen && (
          <RoleFormModal
            isOpen={modal.isOpen}
            mode={modal.mode}
            type={modal.type}
            editingRole={modal.editingRole}
            availableRoles={availableRoles}
            assignedRoleLevels={assignedRoleLevels}
            // Owner modal: exclude roles already assigned to "other roles"
            // Other-roles modal: exclude the owner role + any other-role names (keep own role in edit mode)
            excludedRoles={
              modal.type === 'owner'
                ? otherRoleNames
                : [
                    ownerRoleName,
                    ...otherRoles
                      .filter(r => modal.editingRole?.role !== r.config.role)
                      .map(r => r.config.role)
                  ].filter((r): r is string => typeof r === 'string')
            }
            isSaving={isSaving}
            onClose={closeModal}
            onSave={handleSave}
            roleDisplayNames={orgRoles?.roleDisplayNames}
            isCOCRecommendationRequired={isRecommendationRequired}
            isJournalLocked={isJournalLocked}
          />
        )}

        {/* Delete confirmation modal */}
        <Modal
          isOpen={deleteConfirm.isOpen}
          onClose={() => setDeleteConfirm({ isOpen: false, entry: null })}
          size="md"
        >
          <ModalOverlay />
          <ModalContent>
            <ModalCloseButton />
            <ModalBody>
              <Flex flexDirection="column" gap={4} padding={2}>
                <Box fontSize="40px" color="red.500">
                  <FontAwesomeIcon icon={faTrash} fontSize="42px" />
                </Box>
                <Text textStyle="headingGroup">Delete Role</Text>
                <Text textStyle="textBody">
                  Are you sure you want to remove{' '}
                  <strong>{deleteConfirm.entry?.label ?? 'this role'}</strong>?
                  This action cannot be undone.
                </Text>
                <Flex flexDirection="row" gap={2} justifyContent="flex-end">
                  <MRButton
                    onClick={() =>
                      setDeleteConfirm({ isOpen: false, entry: null })
                    }
                    variant="secondary"
                  >
                    Cancel
                  </MRButton>
                  <MRButton
                    onClick={() => deleteConfirm.entry?.onConfirm()}
                    isLoading={isSaving}
                  >
                    Delete
                  </MRButton>
                </Flex>
              </Flex>
            </ModalBody>
          </ModalContent>
        </Modal>
      </OutlineCard>
    </Flex>
  );
};

export default CocConfiguration;
